<template>
  <div class="maintainance-standard-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('maintainanceStandard.page.title')" :subtitle="$t('maintainanceStandard.page.subtitle')">
      <template #extra>
        <el-button type="primary" icon="el-icon-plus" @click="handleAdd">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.standardName" clearable class="w-full"
            :placeholder="$t('maintainanceStandard.search.standardName')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('maintainanceStandard.list.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('maintainanceStandard.page.empty')" @retry="getList">
        <el-table-column prop="standardName" :label="$t('maintainanceStandard.table.standardName')" align="left"
          min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.standardName || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('maintainanceStandard.table.itemCount')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.itemCount }}</span>
            <el-link type="primary" class="ds-link" @click="handleItem(scope.row)">
              {{ $t('maintainanceStandard.table.item') }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('maintainanceStandard.table.remark')" align="left" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('maintainanceStandard.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ formatDateTimeSec(scope.row.createTime) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="140" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('maintainanceStandard.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <!-- 组件 -->
    <AddMaintainanceStandard ref="addDialog" @success="handleSuccess" />
    <EditMaintainanceStandard ref="editDialog" @success="handleSuccess" />
    <DeleteMaintainanceStandard ref="deleteDialog" @success="handleSuccess" />
  </div>
</template>

<script>
import {
  listMaintainanceStandard
} from '@/api/inspection/maintainanceStandardManageApi'
import AddMaintainanceStandard from '@/components/inspection/addMaintainanceStandard'
import EditMaintainanceStandard from '@/components/inspection/editMaintainanceStandard'
import DeleteMaintainanceStandard from '@/components/inspection/deleteMaintainanceStandard'
import { getCommunityId } from '@/api/community/communityApi'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'MaintainanceStandardManageList',
  components: {
    AddMaintainanceStandard,
    EditMaintainanceStandard,
    DeleteMaintainanceStandard,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        standardName: '',
        communityId: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.searchForm.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, total } = await listMaintainanceStandard(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('获取保养标准失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('maintainanceStandard.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.standardName = ''
      this.page.current = 1
      this.getList()
    },
    handleAdd() {
      this.$refs.addDialog.open()
    },
    handleEdit(row) {
      this.$refs.editDialog.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteDialog.open(row)
    },
    handleItem(row) {
      this.$router.push(`/views/inspection/maintainanceStandardItem?standardId=${row.standardId}`)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    },
    /**
     * 创建时间格式化（后端返回 "yyyy-MM-dd HH:mm:ss" 字符串）
     */
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.maintainance-standard-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 检查项链接 */
.ds-link {
  margin-left: var(--ds-spacing-xs);
  font-weight: 500;
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
